import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import BotToggle from "../components/BotToggle";
import PortfolioCard from "../components/PortfolioCard";
import PriceChart from "../components/PriceChart";
import SignalPanel from "../components/SignalPanel";
import PositionsTable from "../components/PositionsTable";
import AIHealthPanel from "../components/AIHealthPanel";
import AIComparisonPanel from "../components/AIComparisonPanel";
import NeurobroPanel from "../components/NeurobroPanel";
import SymbolSelector from "../components/SymbolSelector";
import { Button } from "../components/ui/button";
import { ArrowCounterClockwise, Broom } from "@phosphor-icons/react";
import { toast } from "sonner";
import { endpoints } from "../lib/api";

export default function Dashboard() {
  const [symbol, setSymbol] = useState("BTCUSDT");
  const [refreshKey, setRefreshKey] = useState(0);
  const [interval, setInterval] = useState("15m");
  const [settings, setSettings] = useState(null);
  const [dataSrc, setDataSrc] = useState(null);

  useEffect(() => {
    endpoints.settings().then((r) => setSettings(r.data)).catch(() => {});
  }, [refreshKey]);

  useEffect(() => {
    endpoints.dataSource().then((r) => setDataSrc(r.data)).catch(() => {});
    const id = window.setInterval(() => {
      endpoints.dataSource().then((r) => setDataSrc(r.data)).catch(() => {});
    }, 30000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar />
      <main className="flex-1 p-6 space-y-5 overflow-x-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Command Center
            </div>
            <h1 className="font-heading font-black text-3xl tracking-tight">Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="dot-live" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Live · Binance
              {dataSrc?.active === "futures" ? (
                <span className="text-signal-buy ml-1">Futures</span>
              ) : dataSrc?.active === "spot" ? (
                <span className="text-signal-warn ml-1">Spot (Futures blocked)</span>
              ) : (
                <span className="text-signal-sell ml-1">Offline</span>
              )}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8">
            <BotToggle onChange={() => setRefreshKey((k) => k + 1)} />
          </div>
          <div className="lg:col-span-4">
            <div className="border border-border rounded-sm bg-[#0d0d0d] p-4 h-full">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                Bot Config Snapshot
              </div>
              {settings ? (
                <div className="space-y-2 font-mono text-[11px]">
                  <Row k="Strategy" v={(settings.strategy || "swing").toUpperCase()} />
                  <Row k="Timeframe" v={settings.kline_interval || "15m"} />
                  <Row k="Tick" v={`${settings.tick_seconds || 20}s`} />
                  <Row k="Mode" v={settings.mode.toUpperCase()} />
                  <Row k="Leverage" v={`${settings.leverage}x`} />
                  <Row k="Take Profit" v={`${settings.tp_pct}%`} />
                  <Row k="Stop Loss" v={`${settings.sl_pct}%`} />
                  <Row k="Risk/Trade" v={`${settings.risk_per_trade_pct}%`} />
                  <Row k="Sinyal" v={(settings.signal_source || "neurobro") === "claude" ? "CLAUDE AI" : "NEUROBRO AI"} />
                  <Row k="Watched" v={`${settings.watched_symbols.length} coins`} />
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">Loading...</div>
              )}
            </div>
          </div>
        </div>

        <PortfolioCard refreshKey={refreshKey} onReset={() => setRefreshKey((k) => k + 1)} />

        <AIComparisonPanel refreshKey={refreshKey} />

        {/* Metric Reset Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-sm bg-[#0d0d0d] px-4 py-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Manual Reset · Hover metric card to reset individually, or use the buttons here
          </div>
          <div className="flex flex-wrap gap-2">
            <ResetBtn label="Profit" testid="reset-profit-btn" onClick={async () => {
              if (!window.confirm("Reset Realized Profit to $0?")) return;
              await endpoints.resetMetric("pnl");
              toast.success("Profit reset");
              setRefreshKey((k) => k + 1);
            }} />
            <ResetBtn label="Total P&L" testid="reset-totalpnl-btn" onClick={async () => {
              if (!window.confirm("Reset Total P&L to $0?")) return;
              await endpoints.resetMetric("pnl");
              toast.success("Total P&L reset");
              setRefreshKey((k) => k + 1);
            }} />
            <ResetBtn label="Winrate" testid="reset-winrate-btn" onClick={async () => {
              if (!window.confirm("Reset Winrate counters to 0W/0L?")) return;
              await endpoints.resetMetric("winrate");
              toast.success("Winrate reset");
              setRefreshKey((k) => k + 1);
            }} />
            <ResetBtn label="History" testid="reset-history-btn" onClick={async () => {
              if (!window.confirm("Delete ALL closed trades from History? This cannot be undone.")) return;
              await endpoints.resetMetric("history");
              toast.success("History cleared");
              setRefreshKey((k) => k + 1);
            }} />
            <Button
              data-testid="reset-all-btn"
              onClick={async () => {
                if (!window.confirm("Reset Profit, Total P&L, Winrate, AND History all at once?")) return;
                await endpoints.resetMetric("all");
                toast.success("All metrics reset");
                setRefreshKey((k) => k + 1);
              }}
              variant="outline"
              size="sm"
              className="rounded-sm border-signal-warn/40 text-signal-warn hover:bg-signal-warn/10 hover:text-signal-warn h-8"
            >
              <Broom size={12} className="mr-1.5" weight="bold" />
              Reset All
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SymbolSelector value={symbol} onChange={setSymbol} />
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Selected pair for chart & analysis
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8">
            <PriceChart symbol={symbol} onIntervalChange={setInterval} />
          </div>
          <div className="lg:col-span-4 space-y-4">
            <AIHealthPanel />
            <SignalPanel symbol={symbol} interval={interval} />
            <NeurobroPanel symbol={symbol} />
          </div>
        </div>

        <PositionsTable onChange={() => setRefreshKey((k) => k + 1)} />
      </main>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between border-b border-border pb-1.5">
    <span className="text-muted-foreground uppercase">{k}</span>
    <span className="text-white">{v}</span>
  </div>
);

const ResetBtn = ({ label, onClick, testid }) => (
  <Button
    data-testid={testid}
    onClick={onClick}
    variant="outline"
    size="sm"
    className="rounded-sm h-8 border-border text-muted-foreground hover:text-white hover:border-signal-brand/40 hover:bg-signal-brand/5"
  >
    <ArrowCounterClockwise size={12} className="mr-1.5" weight="bold" />
    {label}
  </Button>
);
